import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../Components/AuthContext';
import { Package, Pencil, PowerOff, Plus, X } from 'lucide-react';
import MainLayout from '../../Components/Layout/MainLayout/MainLayout';
import { Button } from '../../Components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../../Components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetBody, SheetFooter } from '../../Components/ui/sheet';

const inputClass = "h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";

const DISCOUNT_TYPES = ['frequency', 'home_gym', 'referral'];
const DISCOUNT_TYPE_LABEL = { frequency: 'Frequency', home_gym: 'Home Gym', referral: 'Referral' };

const EMPTY_DISCOUNT = { name: '', discountType: 'frequency', discountValue: '' };
const EMPTY_FORM = {
  name: '',
  description: '',
  sessionsPerWeek: '',
  sessionCredits: '',
  basePricePerSession: '',
  isActive: true,
  discounts: [],
};

const Packages = () => {
  const { tenantDomain } = useParams();
  const { updateTenantDomain } = useAuth();

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [existingDiscounts, setExistingDiscounts] = useState([]);
  const [copyFrom, setCopyFrom] = useState('');

  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  const fetchPackages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/package`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`${response.status}`);
      const data = await response.json();
      setPackages(data);
    } catch (err) {
      setError('Failed to load packages. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [tenantDomain]);

  const fetchExistingDiscounts = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/package/discounts`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) return;
      const data = await response.json();
      setExistingDiscounts(data);
    } catch {
      // non-fatal: copy-from dropdown just won't populate
    }
  }, [tenantDomain]);

  useEffect(() => {
    updateTenantDomain(tenantDomain);
    fetchPackages();
  }, [tenantDomain]);

  const openCreateSheet = () => {
    setEditingPackage(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setCopyFrom('');
    fetchExistingDiscounts();
    setSheetOpen(true);
  };

  const openEditSheet = (pkg) => {
    setEditingPackage(pkg);
    setForm({
      name: pkg.name,
      description: pkg.description ?? '',
      sessionsPerWeek: pkg.sessionsPerWeek != null ? String(pkg.sessionsPerWeek) : '',
      sessionCredits: String(pkg.sessionCredits),
      basePricePerSession: String(pkg.basePricePerSession),
      isActive: pkg.isActive,
      discounts: pkg.discounts.map(d => ({
        name: d.name,
        discountType: d.discountType,
        discountValue: String(d.discountValue),
      })),
    });
    setFormError(null);
    setCopyFrom('');
    fetchExistingDiscounts();
    setSheetOpen(true);
  };

  const addDiscountRow = () => {
    setForm(f => ({ ...f, discounts: [...f.discounts, { ...EMPTY_DISCOUNT }] }));
  };

  const removeDiscountRow = (index) => {
    setForm(f => ({ ...f, discounts: f.discounts.filter((_, i) => i !== index) }));
  };

  const updateDiscount = (index, field, value) => {
    setForm(f => {
      const discounts = [...f.discounts];
      discounts[index] = { ...discounts[index], [field]: value };
      return { ...f, discounts };
    });
  };

  const handleCopyFrom = (e) => {
    const discountId = e.target.value;
    if (!discountId) return;
    const source = existingDiscounts.find(d => d.discountId === discountId);
    if (!source) return;
    setForm(f => ({
      ...f,
      discounts: [
        ...f.discounts,
        { name: source.name, discountType: source.discountType, discountValue: String(source.discountValue) },
      ],
    }));
    setCopyFrom('');
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.sessionCredits || !form.basePricePerSession) {
      setFormError('Name, session credits, and base price are required.');
      return;
    }
    if (isNaN(Number(form.sessionCredits)) || Number(form.sessionCredits) <= 0) {
      setFormError('Session credits must be a positive number.');
      return;
    }
    if (isNaN(Number(form.basePricePerSession)) || Number(form.basePricePerSession) < 0) {
      setFormError('Base price must be a non-negative number.');
      return;
    }
    for (const d of form.discounts) {
      if (!d.name.trim() || !d.discountType || d.discountValue === '') {
        setFormError('Each discount must have a name, type, and value.');
        return;
      }
      if (isNaN(Number(d.discountValue)) || Number(d.discountValue) < 0) {
        setFormError('Discount values must be non-negative numbers.');
        return;
      }
    }

    setSaving(true);
    setFormError(null);
    try {
      const isEdit = !!editingPackage;
      const url = isEdit
        ? `${process.env.REACT_APP_API_URL}/${tenantDomain}/package/${editingPackage.packageId}`
        : `${process.env.REACT_APP_API_URL}/${tenantDomain}/package`;

      const body = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        sessionsPerWeek: form.sessionsPerWeek !== '' ? Number(form.sessionsPerWeek) : null,
        sessionCredits: Number(form.sessionCredits),
        basePricePerSession: Number(form.basePricePerSession),
        discounts: form.discounts.map(d => ({
          name: d.name.trim(),
          discountType: d.discountType,
          discountValue: Number(d.discountValue),
        })),
        ...(isEdit && { isActive: form.isActive }),
      };

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(`${response.status}`);
      await fetchPackages();
      setSheetOpen(false);
    } catch (err) {
      setFormError('Failed to save package. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    setDeactivating(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/${tenantDomain}/package/${deactivateTarget.packageId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!response.ok) throw new Error(`${response.status}`);
      await fetchPackages();
      setDeactivateTarget(null);
    } catch (err) {
      setError('Failed to deactivate package. Please try again.');
    } finally {
      setDeactivating(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="text-sm text-muted-foreground py-8">Loading...</div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Packages</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your session packages and pricing</p>
        </div>
        <Button onClick={openCreateSheet}>Add Package</Button>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">{error}</div>
      )}

      {packages.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Package size={36} className="text-muted-foreground mb-3" />
          <p className="text-sm font-medium">No packages yet</p>
          <p className="text-sm text-muted-foreground mt-1">Create your first package to start invoicing clients.</p>
          <Button className="mt-4" onClick={openCreateSheet}>Add Package</Button>
        </div>
      ) : (
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Name</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Credits</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Base price/session</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground hidden sm:table-cell">Sessions/week</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Discounts</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {packages.map((pkg) => (
                <tr
                  key={pkg.packageId}
                  className={`border-b last:border-0 transition-opacity ${!pkg.isActive ? 'opacity-50' : ''}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium">{pkg.name}</div>
                    {pkg.description && (
                      <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{pkg.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{pkg.sessionCredits}</td>
                  <td className="px-4 py-3 text-right tabular-nums">${pkg.basePricePerSession.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right tabular-nums hidden sm:table-cell">
                    {pkg.sessionsPerWeek != null ? `${pkg.sessionsPerWeek}x` : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {pkg.discounts.length === 0 ? (
                      <span className="text-muted-foreground">None</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {pkg.discounts.map((d, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground"
                            title={`${d.name} — $${d.discountValue.toFixed(2)}/session`}
                          >
                            {DISCOUNT_TYPE_LABEL[d.discountType]} −${d.discountValue.toFixed(2)}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {pkg.isActive ? (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700">Active</span>
                    ) : (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEditSheet(pkg)}>
                        <Pencil size={13} className="mr-1.5" /> Edit
                      </Button>
                      {pkg.isActive && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeactivateTarget(pkg)}
                          className="border-destructive/40 text-destructive hover:bg-destructive/10"
                        >
                          <PowerOff size={13} className="mr-1.5" /> Deactivate
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{editingPackage ? editingPackage.name : 'Add Package'}</SheetTitle>
          </SheetHeader>
          <SheetBody>
            <div className="flex flex-col gap-4">
              {formError && (
                <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">{formError}</div>
              )}

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Name <span className="text-destructive">*</span></label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputClass}
                  placeholder="e.g. 8-Session Solo Package"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 resize-none"
                  rows={2}
                  placeholder="Optional description..."
                />
              </div>

              <div className="flex gap-3">
                <div className="flex flex-col gap-1.5 flex-1">
                  <label className="text-sm font-medium">Session credits <span className="text-destructive">*</span></label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={form.sessionCredits}
                    onChange={(e) => setForm({ ...form, sessionCredits: e.target.value })}
                    className={inputClass}
                    placeholder="8"
                  />
                </div>
                <div className="flex flex-col gap-1.5 flex-1">
                  <label className="text-sm font-medium">Sessions/week</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={form.sessionsPerWeek}
                    onChange={(e) => setForm({ ...form, sessionsPerWeek: e.target.value })}
                    className={inputClass}
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Base price per session ($) <span className="text-destructive">*</span></label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.basePricePerSession}
                  onChange={(e) => setForm({ ...form, basePricePerSession: e.target.value })}
                  className={inputClass}
                  placeholder="75.00"
                />
              </div>

              {editingPackage && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="pkg-is-active"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    className="h-4 w-4 rounded border-input"
                  />
                  <label htmlFor="pkg-is-active" className="text-sm font-medium">Active</label>
                </div>
              )}

              {/* Discounts section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Discounts</p>
                  <Button type="button" variant="outline" size="sm" onClick={addDiscountRow}>
                    <Plus size={13} className="mr-1" /> Add
                  </Button>
                </div>

                {existingDiscounts.length > 0 && (
                  <div className="mb-3">
                    <select
                      value={copyFrom}
                      onChange={handleCopyFrom}
                      className={inputClass}
                    >
                      <option value="">Copy from existing discount...</option>
                      {existingDiscounts.map((d) => (
                        <option key={d.discountId} value={d.discountId}>
                          {d.name} — {DISCOUNT_TYPE_LABEL[d.discountType]} −${d.discountValue.toFixed(2)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {form.discounts.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No discounts. Click Add to create one.</p>
                ) : (
                  <div className="flex flex-col gap-3">
                    {form.discounts.map((d, i) => (
                      <div key={i} className="flex flex-col gap-2 rounded-md border p-3 bg-muted/20">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium text-muted-foreground">Discount {i + 1}</span>
                          <button
                            type="button"
                            onClick={() => removeDiscountRow(i)}
                            className="text-muted-foreground hover:text-destructive transition-colors"
                          >
                            <X size={14} />
                          </button>
                        </div>
                        <input
                          type="text"
                          value={d.name}
                          onChange={(e) => updateDiscount(i, 'name', e.target.value)}
                          className={inputClass}
                          placeholder="Discount name"
                        />
                        <div className="flex gap-2">
                          <select
                            value={d.discountType}
                            onChange={(e) => updateDiscount(i, 'discountType', e.target.value)}
                            className={`${inputClass} flex-1`}
                          >
                            {DISCOUNT_TYPES.map(t => (
                              <option key={t} value={t}>{DISCOUNT_TYPE_LABEL[t]}</option>
                            ))}
                          </select>
                          <div className="relative flex-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={d.discountValue}
                              onChange={(e) => updateDiscount(i, 'discountValue', e.target.value)}
                              className={`${inputClass} pl-6`}
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </SheetBody>
          <SheetFooter>
            <Button variant="outline" onClick={() => setSheetOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Deactivate Confirmation Dialog */}
      <Dialog open={!!deactivateTarget} onOpenChange={(open) => { if (!open) setDeactivateTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate Package</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to deactivate <strong>{deactivateTarget?.name}</strong>? Existing client packages will not be affected.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateTarget(null)} disabled={deactivating}>Cancel</Button>
            <Button
              onClick={handleDeactivate}
              disabled={deactivating}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deactivating ? 'Deactivating...' : 'Deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Packages;
